import { useEffect, useState } from 'react'
import { notificationsApi, type NotificationPreferenceItem, type NotificationCategoryItem } from '../../services/api'
import { ensurePushSubscription, unsubscribePush } from '../../lib/webPush'
import { getNotificationPermission, requestNotificationPermission } from '../../lib/browserNotifications'

// OSPROD-NOTIF-L7: settings panel — toggle (category × channel)
// preferences. Categories load from the server registry (not hard-coded)
// so each producer stage that adds a category surfaces here without
// touching the UI.

type Channel = NotificationPreferenceItem['channel']

const ALL_CHANNELS: Channel[] = ['email', 'sms', 'push', 'inApp']

export default function NotificationsSettingsSection() {
  const [categories, setCategories] = useState<NotificationCategoryItem[]>([])
  const [prefs, setPrefs] = useState<Map<string, boolean>>(new Map())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [pushPerm, setPushPerm] = useState<string>(getNotificationPermission())

  useEffect(() => {
    let cancelled = false
    Promise.all([
      notificationsApi.getCategories(),
      notificationsApi.getPreferences(),
    ])
      .then(([catRes, prefRes]) => {
        if (cancelled) return
        setCategories(catRes.data.categories)
        const map = new Map<string, boolean>()
        for (const p of prefRes.data.preferences) map.set(`${p.category}::${p.channel}`, p.enabled)
        setPrefs(map)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const isEnabled = (category: string, channel: Channel): boolean => {
    const v = prefs.get(`${category}::${channel}`)
    return v === undefined ? true : v
  }

  const toggle = async (category: string, channel: Channel) => {
    const next = !isEnabled(category, channel)
    setPrefs((prev) => {
      const m = new Map(prev)
      m.set(`${category}::${channel}`, next)
      return m
    })
    setSaving(true)
    try {
      await notificationsApi.savePreferences([{ category, channel, enabled: next }])
    } finally {
      setSaving(false)
    }
  }

  const enableBrowser = async () => {
    const result = await requestNotificationPermission()
    setPushPerm(result)
    if (result === 'granted') await ensurePushSubscription()
  }

  const disableBrowser = async () => {
    await unsubscribePush()
    setPushPerm(getNotificationPermission())
  }

  // Show only channel columns that at least one category supports.
  const shownChannels = ALL_CHANNELS.filter((ch) =>
    categories.some((c) => c.channels.includes(ch)),
  )

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-white text-[15px] font-medium">Browser notifications</div>
        <div className="text-[12px] text-[#80848e] mb-2">Status: <span className="text-[#dbdee1]">{pushPerm}</span></div>
        {pushPerm === 'default' && <button onClick={enableBrowser} className="px-3 py-1 rounded bg-[#5865f2] text-white text-[13px]">Enable browser notifications</button>}
        {pushPerm === 'granted' && <button onClick={disableBrowser} className="px-3 py-1 rounded bg-[#2b2d31] text-[#dbdee1] text-[13px]">Unsubscribe this device</button>}
        {pushPerm === 'denied' && <div className="text-[12px] text-[#ef4444]">Browser notifications are blocked. Enable in your browser site settings.</div>}
        {pushPerm === 'unsupported' && <div className="text-[12px] text-[#80848e]">This browser does not support push notifications.</div>}
      </div>

      <div className="border-t border-[#2b2d31]" />

      <div>
        <div className="text-white text-[15px] font-medium">Notification channels</div>
        <div className="text-[12px] text-[#80848e] mb-3">Toggle which channels you want to receive each kind of notification on.</div>
        {loading ? (
          <div className="text-[12px] text-[#80848e]">Loading…</div>
        ) : categories.length === 0 ? (
          <div className="text-[12px] text-[#80848e]">No notification categories configured yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[11px] text-[#80848e] uppercase">
                  <th className="text-left py-2 font-medium">Category</th>
                  {shownChannels.map((c) => <th key={c} className="px-3 py-2 font-medium">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.key} className="border-t border-[#2b2d31]">
                    <td className="py-2 text-[13px] text-[#dbdee1]">
                      <div>{cat.label}</div>
                      <div className="text-[11px] text-[#80848e]">{cat.description}</div>
                    </td>
                    {shownChannels.map((ch) => (
                      <td key={ch} className="px-3 text-center">
                        {cat.channels.includes(ch) ? (
                          <input
                            type="checkbox"
                            checked={isEnabled(cat.key, ch)}
                            onChange={() => toggle(cat.key, ch)}
                            disabled={saving}
                          />
                        ) : (
                          <span className="text-[#3a3b3f]">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
